<template>
  <div class="multisteps-form__panel border-radius-xl bg-white" data-animation="FadeIn">
    <h5 class="font-weight-bolder">{{ $t('eartag.Find_and_select_keeper') }}</h5>    
    <div class="multisteps-form__content">
      <div class="row mt-3">
        <div class="col-12">
          <div class="mb-4">
            <p class="fw-bold text-sm">{{ $t('eartag.Selected_keeper') }} ({{ selectedKeepers.length }})</p>
            <div class="row">
              <div v-for="keeper in selectedKeepers" :key="keeper.id" class="col-md-6 mb-1">
                <div class="card border  text-sm border-success bg-light" @click="toggleSelect(keeper)">                  
                  <span v-html="displayText(keeper)"></span>
                  <span 
                    class="text-danger position-absolute top-1 end-0"
                    style="cursor: pointer; font-weight: bold; font-size: 25px; margin-right: 10px;"
                    @click.stop="toggleSelect(keeper)"
                  >
                    ×
                  </span>
                </div>
              </div>              
              <p class="text-danger text-sm" v-if="v$.selectedKeeperId.$error">
                {{ v$.selectedKeeperId.$errors[0].$message }}
              </p>
            </div>
          </div>
        </div>
        <div class="col-6">
          <!-- Search Input -->
          <div class="input-group input-group-static null">
            <label class="">{{ $t('eartag.Search') }}</label>
            <input v-model="searchQuery" type="text" class="form-control form-control-default" :placeholder="$t('eartag.Search_name_RVORelationnumber')"> 
          </div>
        </div>
        <div class="col-12 mt-3">
          <!-- Available keepers -->
          <p class="fw-bold text-sm">{{ $t('eartag.Available_keepers') }}</p>
          <div class="row">
            <div v-for="keeper in filteredKeepers" :key="keeper.id" class="col-md-6 mb-1">
              <div class="card border text-sm" :class="{ 'border-success bg-light': isSelected(keeper.id), 'cursor-pointer': true }" @click="toggleSelect(keeper)">                
                <span v-html="displayText(keeper)"></span>
              </div>
            </div>
          </div>
        </div>
      </div>      
      <div class="button-row d-flex mt-4">        
        <button class="btn mb-0 btn-sm bg-gradient-success null null ms-auto js-btn-next" type="button" @click="validateAndNextStep">{{ $t('common.Next') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
  import { computed, reactive, ref, watch } from 'vue'
  import { required, helpers } from '@vuelidate/validators'
  import useVuelidate from '@vuelidate/core'
  import { useI18n } from 'vue-i18n'
  import { useStore } from 'vuex'

  export default {
    name: "ReTaggingKeeper",
    props: {
      keepers: Array,
      formData: Object,
      prevStep: Function,
      nextStep: Function
    },
    setup(props, { emit }) {
      const { t } = useI18n()
      const store = useStore()
      const localFormData = reactive(props.formData)
      
      const searchQuery = ref('')

      const selectedKeeperId = ref(
        Array.isArray(localFormData.KeeperId)
          ? localFormData.KeeperId
          : (localFormData.KeeperId ? [localFormData.KeeperId] : [])
      )

      const isSelected = (locationId) => selectedKeeperId.value.includes(locationId)

      const toggleSelect = (keeper) => {
        selectedKeeperId.value = isSelected(keeper.locationId) ? [] : [keeper.locationId]
      }
      
      const displayText = (f) => {
        const role = store.state.roleName;
        let parts = [];

        if(role === 'Admin')
        {
          if (f.associationAbbreviation) parts.push(`(${f.associationAbbreviation})`);
          if (f.associationName) parts.push(f.associationName);
        }
        if (f.rvoRelationnumber) parts.push(f.rvoRelationnumber);
        if (f.fullname) parts.push(f.fullname);
        if (f.ubn) parts.push(f.ubn);
        
        return parts.join(' | ');
      }
      
      const selectedKeepers = computed(() =>
        (props.keepers || []).filter(f => selectedKeeperId.value.includes(f.locationId))
      )


      const filteredKeepers = computed(() =>
        (props.keepers || []).filter(f =>
          !selectedKeeperId.value.includes(f.locationId) &&
          displayText(f).toLowerCase().includes((searchQuery.value || '').toLowerCase())
        )
      )

      const hydrateFromSelection = () => {
        const selId = selectedKeeperId.value[0]
        if (!selId) return
        const selected = (props.keepers || []).find(a => a.locationId === selId)
        if (!selected) return

        localFormData.KeeperId = selId
        localFormData.rvoRelationnumber = selected?.rvoRelationnumber ?? localFormData.rvoRelationnumber ?? ''
        localFormData.keeperUBN = selected?.keeperUBN ?? selected?.ubn ?? localFormData.keeperUBN ?? ''
        localFormData.keeperDetails = displayText(selected)
      }

      watch(localFormData, () => emit('update:formData', localFormData), { deep: true })

      watch(selectedKeeperId, hydrateFromSelection, { immediate: true })
      watch(() => props.keepers, hydrateFromSelection, { immediate: true, deep: true })

      // Vuelidate rules – require at least one selected keeper
      const rules = computed(() => ({
        selectedKeeperId: {
          required: helpers.withMessage(t('eartag.No_keeper_selected'), required)
        }
      }))
            
      const v$ = useVuelidate(rules, { selectedKeeperId })

      watch(localFormData, () => {
        emit('update:formData', localFormData)
      }, { deep: true })      

      const validateAndNextStep = async () => {
        v$.value.$touch()
        if (v$.value.$pending) {
          await v$.value.$validate()
        }
        if (!v$.value.$invalid) {

          hydrateFromSelection()

          //const selId = selectedKeeperId.value[0]
          //const selected = (props.keepers || []).find(a => a.locationId === selId)

          // use optional chaining instead of [0]
          //localFormData.KeeperId = selId || ''
          //localFormData.rvoRelationnumber = selected?.rvoRelationnumber || ''
          // support either `keeperUBN` or `ubn` field name
          //localFormData.keeperUBN = selected?.keeperUBN ?? selected?.ubn ?? ''
          //localFormData.keeperDetails = selected ? displayText(selected) : ''

          props.nextStep()
        }
      }

      return {
        localFormData,
        searchQuery,
        selectedKeeperId,
        selectedKeepers,
        filteredKeepers,
        isSelected,
        toggleSelect,
        displayText,
        v$,        
        validateAndNextStep
      }
    }    
};
</script>
<style scoped>
  .card {
    transition: background-color 0.2s ease-in-out;
    cursor: pointer;
    padding: 10px !important;
  }
</style>